"use client";

import conversationalService from "@/api/services/conversational.service";
import { useSearchParams } from "next/navigation";
import {
  createContext,
  ReactNode,
  useContext,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
  RefObject,
} from "react";

type Status = "idle" | "loading" | "succeeded" | "failed";

type SessionItem = {
  userId: string;
  firstQuestion: string;
  sessionId: string;
  createdAt: string;
};

export type Message = {
  role: "assistant" | "human";
  message: string;
  charts?: any[];
  static_charts?: any[];

  // --- streaming/UX metadata (optional) ---
  id?: string; // for local temp items
  isThinking?: boolean;
  isStreaming?: boolean;
  startedAt?: number;
  firstByteAt?: number;
  latencyMs?: number;

  // from backend mapping
  createdAt?: string;
  updatedAt?: string;
};

export type ConversationalContextType = {
  /* ---------- Sessions ---------- */
  sessions: SessionItem[];
  sessionsStatus: Status;
  sessionsError: string | null;
  loadSessions: () => Promise<void>;

  /* ---------- Active Session ---------- */
  currentSessionId: string | null;
  setCurrentSessionId: (id: string | null) => void;

  /* ---------- Messages of Active Session ---------- */
  messages: Message[];
  messagesStatus: Status;
  messagesError: string | null;
  loadMessages: (sessionId: string) => Promise<void>;

  /* ---------- Streaming & Loading States ---------- */
  isLoading: boolean;
  isStreaming: boolean;
  setIsLoading: (v: boolean) => void;
  setIsStreaming: (v: boolean) => void;
  streamingMsg: Message | null;
  setStreamingMsg: (m: any | null) => void;

  /* ---------- Local Modifiers ---------- */
  addLocalMessage: (msg: Message) => void;
  updateLocalMessage: (id: string, patch: Partial<Message>) => void;
  clearMessages: () => void;

  isNewChat: RefObject<boolean>;
};

const ConversationalContext = createContext<
  ConversationalContextType | undefined
>(undefined);

export function ConversationalProvider({ children }: { children: ReactNode }) {
  /* ---------- States ---------- */

  // sessions
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [sessionsStatus, setSessionsStatus] = useState<Status>("idle");
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  // current session
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // messages of current session
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesStatus, setMessagesStatus] = useState<Status>("idle");
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const isNewChat = useRef<boolean>(searchParams?.get("sid") ? false : true);

  // loading / streaming
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMsg, setStreamingMsg] = useState<Message | null>(null);

  /* ---------- Track latest session id to ignore stale responses ---------- */

  const currentSessionIdRef = useRef<string | null>(currentSessionId);

  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  /* ---------- Fetch Sessions ---------- */

  const loadSessions = useCallback(async () => {
    try {
      setSessionsStatus("loading");
      setSessionsError(null);

      const resp = await conversationalService.getHistory();
      if (!resp?.success) throw new Error(resp?.message);

      setSessions(resp.data);
      setSessionsStatus("succeeded");
    } catch (err: any) {
      setSessionsStatus("failed");
      setSessionsError(err?.message || "Failed to load sessions");
    }
  }, []);

  /* ---------- Fetch Messages (history for a session) ---------- */

const loadMessages = useCallback(async (sessionId: string) => {
  if(isNewChat.current) return
  try {
    setMessagesStatus("loading");
    setMessagesError(null);

    const resp = await conversationalService.getSessionMessages({
      session_id: sessionId,
      page: 1,
      limit: 20,
    });

    if (!resp?.success) {
      throw new Error(resp?.message || "Failed to fetch session messages");
    }

    const msgs: Message[] = (resp.data ?? []).map((m: any) => {
      const role: "assistant" | "human" =
        m.role === "user" ? "human" : "assistant";

      let text = "";
      if (typeof m.content === "string") text = m.content;
      else if (Array.isArray(m.content)) text = m.content.join("\n\n");

      const dynamicCharts = m.charts?.dynamic_charts ?? [];
      const staticCharts = m.charts?.static_chart ?? [];

      return {
        id: m.id,
        role,
        message: text,
        charts: [...dynamicCharts, ...staticCharts],
        static_charts: staticCharts,
        createdAt: m.createdAt,
        updatedAt: m.updatedAt,
      };
    });

    if (currentSessionIdRef.current !== sessionId) return;

    setMessages(msgs);
    setMessagesStatus("succeeded");
  } catch (err: any) {
    if (currentSessionIdRef.current !== sessionId) return;
    setMessagesStatus("failed");
    setMessagesError(err?.message || "Failed to load messages");
  }
}, []); // ⬅️ no isNewChat in deps

  // deps: using only currentSessionIdRef (mutable), so [] is fine

  /* ---------- Local UI Updates (Optimistic / Streaming) ---------- */

  const addLocalMessage = useCallback((msg: Message) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const updateLocalMessage = useCallback(
    (id: string, patch: Partial<Message>) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, ...patch } : m))
      );
    },
    []
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setMessagesStatus("idle");
    setMessagesError(null);
  }, []);

  /* ---------- Memoized Context Value ---------- */

  const value = useMemo<ConversationalContextType>(
    () => ({
      /* sessions */
      sessions,
      sessionsStatus,
      sessionsError,
      loadSessions,

      /* current session */
      currentSessionId,
      setCurrentSessionId,

      /* messages */
      messages,
      messagesStatus,
      messagesError,
      loadMessages,

      /* loading / streaming */
      isLoading,
      isStreaming,
      setIsLoading,
      setIsStreaming,
      streamingMsg,
      setStreamingMsg,

      /* local modifications */
      addLocalMessage,
      updateLocalMessage,
      clearMessages,

      isNewChat,
    }),
    [
      sessions,
      sessionsStatus,
      sessionsError,
      loadSessions,
      currentSessionId,
      messages,
      messagesStatus,
      messagesError,
      loadMessages,
      isLoading,
      isStreaming,
      streamingMsg,
      addLocalMessage,
      updateLocalMessage,
      clearMessages,
      isNewChat,
      // setIsNewChat,
    ]
  );

  return (
    <ConversationalContext.Provider value={value}>
      {children}
    </ConversationalContext.Provider>
  );
}

export function useConversational() {
  const ctx = useContext(ConversationalContext);
  if (!ctx)
    throw new Error(
      "useConversational must be used inside ConversationalProvider"
    );
  return ctx;
}
